import { usePageTitle } from '../hooks/usePageTitle.js';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { api, type AuditLogRow } from '../lib/api.js';
import { useSortable } from '../hooks/useSortable.js';
import { SortableTh } from '../components/SortableTh.js';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function actionColor(action: string): string {
  if (action.includes('login')) return 'var(--accent-green)';
  if (action.includes('logout')) return 'var(--text-dimmed)';
  if (action.includes('rejected')) return 'var(--accent-red)';
  if (action.includes('delete')) return 'var(--accent-red)';
  if (action.includes('update') || action.includes('create')) return 'var(--accent-blue)';
  return 'var(--text-secondary)';
}

export function AuditLogsPage() {
  usePageTitle('Audit Logs');
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const { sortKey, sortDir, toggle, sort } = useSortable<'action' | 'userEmail' | 'resource' | 'ip' | 'createdAt'>('createdAt', 'desc');

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: () => api.listAuditLogs({ limit: 500 }),
    refetchInterval: 30_000,
  });

  const logs: AuditLogRow[] = data?.logs ?? [];

  const searchFiltered = logs.filter((l) => {
    const matchesSearch =
      !search ||
      l.action.includes(search) ||
      (l.userEmail ?? '').includes(search) ||
      (l.resource ?? '').includes(search);
    const matchesAction = !actionFilter || l.action === actionFilter;
    return matchesSearch && matchesAction;
  });
  const filtered = sort(searchFiltered, (l) => l[sortKey as keyof AuditLogRow] as string ?? '');

  const uniqueActions = Array.from(new Set(logs.map((l) => l.action))).sort();

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 items-center gap-3 border-b border-border px-6 shrink-0">
        <Link to="/settings" className="text-xs text-text-dimmed hover:text-text-secondary">
          ← Settings
        </Link>
        <span className="text-text-dimmed">/</span>
        <div>
          <div className="text-xs uppercase tracking-widest text-text-secondary">Admin</div>
          <div className="text-lg font-semibold text-text-primary">Audit Log</div>
        </div>
      </header>

      <section className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by action, user, resource…"
            className="rounded-xl px-3 py-2 text-sm outline-none w-80"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
          />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="rounded-xl px-3 py-2 text-sm outline-none"
            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
          >
            <option value="">All actions</option>
            {uniqueActions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <div className="text-xs text-text-dimmed ml-auto">
            {filtered.length} of {logs.length} events
          </div>
        </div>

        <div
          className="glass rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}
        >
          {isLoading ? (
            <div className="p-8 text-center text-sm text-text-dimmed">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-text-dimmed">No audit logs found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <SortableTh label="Action" sortKey="action" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="User" sortKey="userEmail" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="Resource" sortKey="resource" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="IP" sortKey="ip" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} />
                  <SortableTh label="When" sortKey="createdAt" activeSortKey={sortKey} sortDir={sortDir} onSort={toggle} right />
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => (
                  <tr
                    key={log.id}
                    className={`border-b border-border last:border-0 hover:bg-bg-tertiary transition-colors duration-100 ${log.userId ? 'cursor-pointer' : ''}`}
                    onClick={() => { if (log.userId) void navigate({ to: '/settings/users/$id', params: { id: log.userId } }); }}
                  >
                    <td className="px-5 py-3">
                      <span
                        className="font-mono text-xs font-medium"
                        style={{ color: actionColor(log.action) }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className="font-mono text-xs"
                        style={{ color: log.userId ? 'var(--accent-blue)' : 'var(--text-dimmed)' }}
                      >
                        {log.userEmail ?? (log.userId ? log.userId.slice(0, 8) : '—')}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-text-secondary">
                      {log.resource ? (
                        <span>{log.resource}{log.resourceId ? ` · ${log.resourceId.slice(0, 8)}` : ''}</span>
                      ) : (
                        <span className="text-text-dimmed">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-text-dimmed">
                      {log.ip ?? '—'}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-xs text-text-dimmed" title={new Date(log.createdAt).toLocaleString()}>
                      {timeAgo(log.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
